import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { GuideForm } from './forms/GuideForm';
import { useGuidesStore } from '../../stores/guidesStore';
import { useOrganizersStore } from '../../stores/organizersStore';
import { createUserWithoutSignIn } from '../../lib/firebase/auth';
import { Guide } from '../../types/guide';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { uploadFile } from '../../utils/firebase';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddGuideModal = ({ isOpen, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { createGuide, initializeListener, initialized } = useGuidesStore();
  const { translations } = useLanguage();
  const { organizers, loading: organizersLoading, fetchOrganizers } = useOrganizersStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (userRole === 'admin') {
      fetchOrganizers();
    }
  }, [userRole, fetchOrganizers]);

  // Ensure listener is initialized
  useEffect(() => {
    if (!initialized) {
      const cleanup = initializeListener();
      return () => cleanup();
    }
  }, [initialized, initializeListener]);

  const handleSubmit = async (data: Partial<Guide>) => {
    if (!data.email || !data.password || !data.name || !data.organizerId) {
      setError(translations?.addGuideModal?.requiredFields || 'All fields are required.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const firebaseUser = await createUserWithoutSignIn(data.email, data.password);

      // Upload passport photo if provided
      let passportPhotoUrl = null; // Initialize as null instead of undefined
      if (data.passportFile) {
        const uploadResult = await uploadFile(data.passportFile);
        passportPhotoUrl = uploadResult.url;
      }

      const guideData: Guide = {
        id: firebaseUser.uid,
        name: data.name,
        email: data.email,
        password: data.password,
        organizerId: data.organizerId,
        passportNumber: data.passportNumber || null, // Use null instead of undefined
        nusukEmail: data.nusukEmail || data.email, // Default to email if not provided
        mainPhone: data.mainPhone || null, // Use null instead of undefined
        hajjPhone: data.hajjPhone || null, // Use null instead of undefined
        passportPhotoUrl: passportPhotoUrl, // Use previously initialized variable
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await createGuide(guideData);
      toast.success(translations?.addGuideModal?.successMessage || 'Guide added successfully.');
      onClose();
    } catch (err: any) {
      console.error('Error creating guide:', err);
      setError(err.message || translations?.addGuideModal?.errorMessage || 'An error occurred while adding the guide.');
      toast.error(translations?.addGuideModal?.toastError || 'Failed to add the guide.');
    } finally {
      setIsLoading(false);
    }
  };

  if (organizersLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.addGuideModal?.title || 'Add New Guide'}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}
      <GuideForm 
        onSubmit={handleSubmit} 
        isLoading={isLoading} 
        organizers={organizers}
        userRole={userRole || ''}
        userId={userId || ''}
      />
    </Modal>
  );
};